import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { compact, fmt, share } from "../data/format";

// ------------------------------------------------------------------ sparkline (stat-tile trend)

/** Single-series trend for cards and rows: 2px line, 10% wash, end dot with a surface ring. */
export function Sparkline({
  values,
  height = 44,
  class: cls = "",
  label,
}: {
  values: number[];
  height?: number;
  class?: string;
  label?: string;
}) {
  const W = 300;
  const H = height;
  const pad = 4;
  const n = values.length;
  if (n < 2) return null;
  const pts = values.map((v, i) => [(i / (n - 1)) * W, H - pad - v * (H - pad * 2)] as const);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  const area = `${line}L${W},${H}L0,${H}Z`;
  const last = pts[n - 1];
  return (
    <div class={`spark ${cls}`} style={{ height: `${H}px` }} role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : "true"}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} aria-hidden="true">
        <path d={area} class="spark-area" />
        <path d={line} class="spark-line" vector-effect="non-scaling-stroke" />
      </svg>
      <span class="spark-dot" style={{ top: `${(last[1] / H) * 100}%` }} />
    </div>
  );
}

// ------------------------------------------------------------------ history chart

export interface ChartSeries {
  id: string;
  label: string;
  /** CSS class providing --series colour */
  cls: string;
  values: (number | null)[];
}

function niceStep(range: number, target: number): number {
  const raw = range / Math.max(1, target);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  return (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
}

function useWidth(ref: { current: HTMLElement | null }): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setW(Math.round(entry.contentRect.width)));
    ro.observe(el);
    setW(Math.round(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return w;
}

/**
 * Babies-per-year (linear), rank-per-year (log, #1 at top) or share-per-year (0 to 100%) line
 * chart with a crosshair tooltip that snaps to the nearest year and lists every series.
 * Keyboard: arrow keys.
 */
export function HistoryChart({
  years,
  series,
  mode,
  height = 190,
  rankLabel = "Rank",
  measure,
  band,
}: {
  years: number[];
  series: ChartSeries[];
  /** share: values from 0 to 1, shown as percentages */
  mode: "count" | "rank" | "share";
  height?: number;
  /** what the rank is of, for screen readers: "UK rank" */
  rankLabel?: string;
  /** what's plotted, for screen readers, when it isn't babies, rank or a share of babies */
  measure?: string;
  /** years to shade behind the lines, such as the middle half of a name's babies */
  band?: { from: number; to: number };
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const width = useWidth(wrap);
  const [hover, setHover] = useState<number | null>(null);
  const axisBand = 24;
  const left = mode === "rank" ? 44 : 40;
  const right = 14;
  const top = 12;
  const plotW = Math.max(10, width - left - right);
  const plotH = height;
  const n = years.length;

  const scale = useMemo(() => {
    const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null && v > 0));
    if (mode === "share") {
      return { y: (v: number) => (1 - v) * plotH, ticks: [0, 0.5, 1], fmtTick: (t: number) => `${Math.round(t * 100)}%` };
    }
    if (mode === "count") {
      const max = Math.max(1, ...all);
      const step = niceStep(max, 4);
      const top = Math.ceil(max / step) * step;
      const ticks: number[] = [];
      for (let t = 0; t <= top + 1e-9; t += step) ticks.push(t);
      return { y: (v: number) => plotH - (v / top) * plotH, ticks, fmtTick: compact };
    }
    // rank: log scale, 1 at the top
    const worst = Math.max(10, ...all);
    const best = Math.max(1, Math.min(...all, worst));
    const lo = Math.pow(10, Math.floor(Math.log10(best)));
    const hi = Math.pow(10, Math.ceil(Math.log10(worst + 1)));
    const span = Math.log10(hi) - Math.log10(lo) || 1;
    const ticks: number[] = [];
    for (let t = lo; t <= hi; t *= 10) ticks.push(t);
    return {
      y: (v: number) => ((Math.log10(v) - Math.log10(lo)) / span) * plotH,
      ticks,
      fmtTick: (t: number) => (t === 1 ? "#1" : `#${compact(t)}`),
    };
  }, [series, mode, plotH]);

  const x = (i: number) => (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);

  const paths = series.map((s) => {
    let d = "";
    let pen = false;
    s.values.forEach((v, i) => {
      if (v === null || (mode === "rank" && v <= 0)) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${scale.y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  });

  // a 10% wash under a single count (or share) series
  const area =
    series.length === 1 && mode !== "rank"
      ? (() => {
          const vals = series[0].values;
          const first = vals.findIndex((v) => v !== null);
          let lastI = vals.length - 1;
          while (lastI >= 0 && vals[lastI] === null) lastI--;
          if (first < 0) return "";
          let d = `M${x(first)},${plotH}`;
          for (let i = first; i <= lastI; i++) d += `L${x(i).toFixed(1)},${scale.y(vals[i] ?? 0).toFixed(1)}`;
          return d + `L${x(lastI)},${plotH}Z`;
        })()
      : "";

  const xTicks = useMemo(() => {
    if (!n) return [];
    // candidate years on round numbers, kept at least 44px apart and clear of both ends
    const minGap = 52;
    const out: number[] = [0];
    const lastX = n <= 1 ? 0 : plotW;
    for (let i = 1; i < n - 1; i++) {
      if (years[i] % 5 !== 0) continue;
      const px = (i / (n - 1)) * plotW;
      const prev = (out[out.length - 1] / Math.max(1, n - 1)) * plotW;
      if (px - prev >= minGap && lastX - px >= minGap) out.push(i);
    }
    if (n > 1) out.push(n - 1);
    return out;
  }, [years, plotW, n]);

  function onMove(ev: PointerEvent) {
    const svg = ev.currentTarget as SVGElement;
    const rect = svg.getBoundingClientRect();
    const px = ev.clientX - rect.left - left;
    const i = Math.round((px / plotW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  }

  function onKey(ev: KeyboardEvent) {
    if (ev.key === "ArrowLeft" || ev.key === "ArrowRight") {
      ev.preventDefault();
      setHover((h) => {
        const cur = h ?? n - 1;
        return Math.max(0, Math.min(n - 1, cur + (ev.key === "ArrowLeft" ? -1 : 1)));
      });
    } else if (ev.key === "Escape") setHover(null);
  }

  const missing = (v: number | null): v is null => v === null || (mode !== "share" && v <= 0);
  const show = (v: number) => (mode === "rank" ? `#${fmt(v)}` : mode === "share" ? share(v) : fmt(v));
  const bandX =
    band && years.includes(band.from) && years.includes(band.to)
      ? { a: x(years.indexOf(band.from)), b: x(years.indexOf(band.to)) }
      : null;

  const singleEnd =
    series.length === 1
      ? (() => {
          const vals = series[0].values;
          let i = vals.length - 1;
          while (i >= 0 && (vals[i] === null || (mode === "rank" && (vals[i] ?? 0) <= 0))) i--;
          if (i >= 0 && mode === "share" && vals[i] === null) return null;
          if (i < 0) return null;
          // sit the label above the last few points so it never crosses the line
          let top = scale.y(vals[i] as number);
          for (let j = Math.max(0, i - 3); j < i; j++) {
            const v = vals[j];
            if (v !== null && (mode === "count" || v > 0)) top = Math.min(top, scale.y(v));
          }
          return { i, v: vals[i] as number, labelY: Math.max(10, top - 10) };
        })()
      : null;

  const tipLeft = hover !== null ? left + x(hover) : 0;
  const totalH = top + plotH + axisBand;

  return (
    <div class="hchart" ref={wrap}>
      {width > 0 && (
        <svg
          width={width}
          height={totalH}
          class="hchart-svg"
          tabIndex={0}
          role="img"
          aria-label={`${measure ?? (mode === "count" ? "Babies per year" : mode === "share" ? "Share per year" : `${rankLabel} per year`)}, ${years[0]} to ${years[n - 1]}. Use arrow keys to read values.`}
          onPointerMove={onMove}
          onPointerDown={onMove}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKey}
          onBlur={() => setHover(null)}
        >
          <g transform={`translate(${left},${top})`}>
            {bandX && <rect x={bandX.a} y={0} width={Math.max(2, bandX.b - bandX.a)} height={plotH} class="hc-band" />}
            {scale.ticks.map((t) => (
              <g key={t}>
                <line
                  x1={0}
                  x2={plotW}
                  y1={scale.y(t)}
                  y2={scale.y(t)}
                  class={(t === 0 && mode !== "rank") || (t === 0.5 && mode === "share") ? "hc-base" : "hc-grid"}
                />
                <text x={-8} y={scale.y(t)} class="hc-tick" text-anchor="end" dominant-baseline="middle">
                  {scale.fmtTick(t)}
                </text>
              </g>
            ))}
            {xTicks.map((i) => (
              <text key={i} x={x(i)} y={plotH + 17} class="hc-tick" text-anchor={i === n - 1 ? "end" : i === 0 ? "start" : "middle"}>
                {years[i]}
              </text>
            ))}
            {area && <path d={area} class={`hc-area ${series[0].cls}`} />}
            {series.map((s, si) => (
              <path key={s.id} d={paths[si]} class={`hc-line ${s.cls}`} />
            ))}
            {singleEnd && hover === null && (
              <g>
                <circle cx={x(singleEnd.i)} cy={scale.y(singleEnd.v)} r={4} class={`hc-dot ${series[0].cls}`} />
                <text x={x(singleEnd.i)} y={singleEnd.labelY} class="hc-endlabel" text-anchor="end">
                  {show(singleEnd.v)}
                </text>
              </g>
            )}
            {hover !== null && (
              <g>
                <line x1={x(hover)} x2={x(hover)} y1={0} y2={plotH} class="hc-cross" />
                {series.map((s) => {
                  const v = s.values[hover];
                  return !missing(v) ? <circle key={s.id} cx={x(hover)} cy={scale.y(v)} r={4} class={`hc-dot ${s.cls}`} /> : null;
                })}
              </g>
            )}
          </g>
        </svg>
      )}
      {hover !== null && width > 0 && (
        <div
          class={`hc-tip ${tipLeft > width / 2 ? "to-left" : ""}`}
          style={{ left: `${tipLeft}px`, top: `${top}px` }}
          role="status"
        >
          <div class="hc-tip-year">{years[hover]}</div>
          {series.map((s) => {
            const v = s.values[hover];
            return (
              <div class="hc-tip-row" key={s.id}>
                <span class={`hc-key ${s.cls}`} />
                <strong>{missing(v) ? "—" : show(v)}</strong>
                <span class="hc-tip-label">{s.label}</span>
              </div>
            );
          })}
        </div>
      )}
      {series.length > 1 && (
        <div class="hc-legend">
          {series.map((s) => (
            <span key={s.id}>
              <i class={`hc-key ${s.cls}`} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
